import DeviceDisconnectedError from '@/errors/deviceDisconnectedError';

import awsRumCustomEvents from '../../consts/aws/awsRumCustomEvents';
import awsRumStatusTypes from '../../consts/aws/awsRumStatusTypes';
import damRequestsTimeouts from '../../consts/dam/damRequestsTimeouts';
import damStatusTypes from '../../consts/dam/damStatusTypes';
import fwErrorTypes from '../../consts/fwu/fwErrorTypes';
import notificationActionTypes from '../../consts/notification/notificationActionTypes';
import routes from '../../consts/routes';
import {strCode} from '../../consts/strCode';
import {firmwareMockService} from '../../containers/DeviceSettingsPage/FirmwareUpdate/services/firmwareUpdateMockService';
import FirmwareUpdateError from '../../errors/firmwareUpdateError';
import {
    selectDeviceBatteryCharge,
    selectDeviceFirmwareReleaseNotes,
    selectDeviceFirmwareVersion,
    selectIotDeviceConnected,
    selectIsDeviceConnectInProgress,
    selectIsDeviceSyncInProgress,
} from '../../state/selectors/iotDevices';
import {selectIsFirmwareMock, selectLanguageCode} from '../../state/selectors/session';
import {
    resetDeviceFirmwareData,
    setIsDeviceConnectInProgress,
    setIsDeviceConnectIsFailed,
    setIsDeviceInitialized,
    setIsDeviceReconnectInProgress,
    setIsDeviceSyncInProgress,
    setIsDeviceSyncIsFailed,
    setIsDeviceSyncStarted,
    updateDeviceFirmwareData,
} from '../../state/slice/iotDevices';
import {showFirmwareUpdateCompleteModal} from '../../state/slice/modal';
import {dispatch, getState} from '../../state/store';
import firmwareUtils from '../../utils/firmwareUtils';
import {notifyKitSyncResult} from '../device/deviceKitNotificationService';
import rumCustomEvents from '../events/rumCustomEvents';
import rumCustomEventsHelpers from '../events/rumCustomEventsHelpers';
import log from '../logger/log';
import notificationService from '../notificationService';
import damAsyncRequestService from './damAsyncRequestService';
import damClientService from './damClientService';
import damDeviceHelpers from './damDeviceHelpers';
import damService from './damService';
import damTokenService from './damTokenService';

const MIN_BATTERY_CHARGE = 30;

const synchronizeDevice = async (dusn, options) => {
    const {isSetup, isNeedRetry} = options || {};
    const isDeviceSyncInProgress = selectIsDeviceSyncInProgress(dusn)(getState());

    const rumParameters = rumCustomEventsHelpers.getStartParameters(awsRumCustomEvents.SYNCHRONIZE);

    if (isDeviceSyncInProgress) {
        log.info(`damDeviceService: Device synchronization already in progress. dusn: ${dusn}`, dusn);
        return;
    }

    dispatch(setIsDeviceSyncInProgress(dusn, true));

    // to reset sync error state if before sync was failed
    dispatch(setIsDeviceSyncIsFailed(dusn, false));

    try {
        damDeviceHelpers.checkIsDeviceConnected(dusn);

        const isSynchronizeRequired = await damDeviceHelpers.checkIsSynchronizeRequired(dusn, isNeedRetry);

        if (!isSynchronizeRequired) return true;

        log.info(`damDeviceService: Start synchronization process. dusn: ${dusn}`, dusn);

        damDeviceHelpers.checkIsDeviceConnected(dusn);

        const clientService = damClientService.getClientService(dusn);

        dispatch(setIsDeviceSyncStarted(dusn, true));

        const requestId = await clientService.postDeviceSynchronize(dusn);

        await damAsyncRequestService.waitAsyncRequestComplete(dusn, requestId, damRequestsTimeouts.SYNCHRONIZE);
        await damService.setDeviceAssets(dusn, true);

        damDeviceHelpers.setSynchronizeUpdateDate(dusn);

        rumCustomEvents.pushSynchronize({
            dusn,
            eventId: requestId,
            status: awsRumStatusTypes.SUCCESS,
            ...rumParameters,
        });

        log.info(`damDeviceService: Success synchronization. dusn: ${dusn}`, dusn);

        dispatch(setIsDeviceSyncIsFailed(dusn, false));

        return true;
    } catch (e) {
        const isDisconnected = e instanceof DeviceDisconnectedError;

        log.warn(`damDeviceService: Failed synchronization.  dusn: ${dusn}`, e?.message, dusn);

        if (!isDisconnected) {
            rumCustomEvents.pushSynchronize({
                dusn,
                eventId: e?.requestId,
                status: awsRumStatusTypes.FAILED,
                ...rumParameters,
            });

            dispatch(setIsDeviceSyncIsFailed(dusn, true));
        }

        if (isSetup) throw e;
    } finally {
        dispatch(setIsDeviceSyncInProgress(dusn, false));

        if (!isSetup) {
            notifyKitSyncResult(dusn);
        }
    }
};

const connectDevice = async (dusn, options) => {
    const {isSetup} = options || {};
    const isDeviceConnectInProgress = selectIsDeviceConnectInProgress(dusn)(getState());

    if (isDeviceConnectInProgress) {
        log.info(`damDeviceService: Device connection already in progress. dusn: ${dusn}`, dusn);
        return;
    }

    dispatch(setIsDeviceConnectInProgress(dusn, true));

    try {
        log.info(`damDeviceService: Start device connection process. dusn: ${dusn}`, dusn);

        await damTokenService.getUserAuthToken(dusn);

        if (!isSetup) {
            await damService.setDeviceAssets(dusn);
        }

        dispatch(setIsDeviceInitialized(dusn, true));

        const wsService = damService.getWebSocketService(dusn);

        await wsService.initWebSocket({serialNumber: dusn, isForce: true, isInitial: true});

        await damService.getDeviceConnectionStatus(dusn, true);

        if (isSetup) {
            await damDeviceHelpers.waitDeviceConnected(dusn);
        }

        await synchronizeDevice(dusn, {isSetup, isNeedRetry: true});

        dispatch(setIsDeviceConnectIsFailed(dusn, false));

        log.info(`damDeviceService: End device connection process. Success. dusn: ${dusn}`, dusn);
    } catch (e) {
        log.error(`damDeviceService: End device connection process. Failed. dusn: ${dusn}`, e?.message, dusn);

        dispatch(setIsDeviceConnectIsFailed(dusn, true));

        throw e;
    } finally {
        dispatch(setIsDeviceConnectInProgress(dusn, false));
    }
};

const reconnectDevice = async (dusn) => {
    try {
        dispatch(setIsDeviceReconnectInProgress(dusn, true));

        await damDeviceHelpers.retryOperation(connectDevice, [dusn]);
    } catch (e) {
        //
    } finally {
        dispatch(setIsDeviceReconnectInProgress(dusn, false));
    }
};

const getFirmwareUpdateAvailability = async (dusn) => {
    const isFirmwareMock = selectIsFirmwareMock(getState());
    const languageCode = selectLanguageCode(getState());

    try {
        log.info(`damDeviceService: Get firmware update availability for device.`, dusn);

        const clientService = damClientService.getClientService(dusn);
        const availableFirmware = isFirmwareMock
            ? await firmwareMockService.getFirmwareUpdateAvailability(dusn)
            : await clientService.getFirmwareUpdateAvailability(dusn, languageCode);

        validateAvailableFirmwareVersion(availableFirmware, dusn);
    } catch (e) {
        log.error(`damDeviceService: Failed to check available firmware for device.`, e, dusn);
        dispatch(
            updateDeviceFirmwareData(dusn, {
                status: awsRumStatusTypes.FAILED,
                errorType: fwErrorTypes.CHECK_FWU_AVAILABILITY,
            })
        );
    }
};

const validateAvailableFirmwareVersion = (availableFirmware, dusn) => {
    const {availableFirmwareVersion, releaseNotes} = availableFirmware || {};

    if (!availableFirmwareVersion) {
        dispatch(updateDeviceFirmwareData(dusn, {availableFirmwareVersion: null, releaseNotes}));
        log.debug(`damDeviceService: No available firmware found.`, dusn);
        return;
    }

    const currentFirmware = selectDeviceFirmwareVersion(dusn)(getState());
    const isAvailableVersionHigher = firmwareUtils.compareVersions(availableFirmwareVersion, currentFirmware) === 1;

    if (isAvailableVersionHigher) {
        dispatch(updateDeviceFirmwareData(dusn, {availableFirmwareVersion, releaseNotes}));
        log.debug(`damDeviceService: Available firmware version is higher than current version.`, dusn);
    } else {
        dispatch(updateDeviceFirmwareData(dusn, {availableFirmwareVersion: null}));
        log.debug(`damDeviceService: Available firmware version is not higher than current version.`, dusn);
    }
};

const checkFirmwarePreconditions = async (dusn) => {
    const isFirmwareMock = selectIsFirmwareMock(getState());

    if (isFirmwareMock) return true;

    const isDeviceStatusUpdated = await damService.getDeviceConnectionStatus(dusn);

    if (!isDeviceStatusUpdated) {
        notificationService.showReconnectDeviceWarning(dusn);
        throw new FirmwareUpdateError(fwErrorTypes.DEVICE_STATUS_NOT_UPDATED);
    }

    const isDeviceConnected = selectIotDeviceConnected(dusn)(getState());

    if (!isDeviceConnected) {
        notificationService.showReconnectDeviceWarning(dusn);
        throw new FirmwareUpdateError(fwErrorTypes.DEVICE_DISCONNECTED);
    }

    const isDeviceAssetsUpdated = await damService.setDeviceAssets(dusn);

    if (!isDeviceAssetsUpdated) {
        notificationService.showRefreshDeviceSettingsWarning(dusn);
        throw new FirmwareUpdateError(fwErrorTypes.DEVICE_ASSETS_NOT_UPDATED);
    }

    const batteryCharge = selectDeviceBatteryCharge(dusn)(getState());

    if (batteryCharge < MIN_BATTERY_CHARGE) {
        notificationService.showToastNotification({dusn, messageStr: strCode.FWU_ERROR_LOW_BATTERY});
        throw new FirmwareUpdateError(fwErrorTypes.LOW_BATTERY);
    }
};

const updateFirmware = async (dusn) => {
    rumCustomEventsHelpers.setFirmwareUpdateParams();

    try {
        log.info('damDeviceService: Start firmware update', dusn);

        await checkFirmwarePreconditions(dusn);

        if (damDeviceHelpers.checkIsFirmwareUpdateInProgress(dusn)) {
            log.info(`damDeviceService: Firmware update already in progress for device. dusn: ${dusn}`, dusn);
            return;
        }

        const isFirmwareMock = selectIsFirmwareMock(getState());

        const clientService = damClientService.getClientService(dusn);
        const requestId = isFirmwareMock
            ? firmwareMockService.postUpdateFirmware(dusn)
            : await clientService.postUpdateFirmware(dusn);

        // useDeviceFirmwareUpdate hook will handle running status
        dispatch(
            updateDeviceFirmwareData(dusn, {progress: 0, status: damStatusTypes.RUNNING, requestId, isManual: true})
        );

        log.info('damDeviceService: Successfully started firmware update', dusn);
    } catch (e) {
        log.error('damDeviceService: Failed to start firmware update', e?.message, dusn);

        firmwareUpdateErrorHandler(dusn, e);
    }
};

const firmwareUpdateSuccessHandler = (dusn, requestId) => {
    log.info('damDeviceService: Firmware update successful', dusn);

    rumCustomEvents.pushFirmwareUpdateSuccess(dusn, requestId);

    const isFwuPageActive = window.location.pathname.includes(routes.DEVICE_FWU);

    if (isFwuPageActive) {
        const releaseNotes = selectDeviceFirmwareReleaseNotes(dusn);

        if (releaseNotes) {
            dispatch(showFirmwareUpdateCompleteModal({isUpdateCompleteSlideActive: true}));
        } else {
            // Handle case: one more firmware update is available after the update (need to check FWU)
            dispatch(resetDeviceFirmwareData(dusn));
        }
        return;
    }

    notificationService.showToastNotification({
        messageStr: strCode.FWU_SUCCESS_UPDATE_NOTIFICATION,
        actionStr: strCode.FWU_SUCCESS_UPDATE_NOTIFICATION_ACTION,
        actionType: notificationActionTypes.FIRMWARE_UPDATE_COMPLETED,
        dusn,
    });
};

const firmwareUpdateErrorHandler = (dusn, e) => {
    log.error(`damDeviceService: Firmware update failed, errorType: ${e?.errorType}`, e?.message, dusn);

    rumCustomEvents.pushFirmwareUpdateFailed(dusn, e);

    const isPreconditionError = fwErrorTypes.PRECONDITIONS.includes(e?.errorType);

    // Don't show error page if precondition error
    if (isPreconditionError) return;

    dispatch(updateDeviceFirmwareData(dusn, {status: damStatusTypes.FAILED, errorType: e?.errorType, isManual: false}));
};

export default {
    connectDevice,
    getFirmwareUpdateAvailability,
    updateFirmware,
    synchronizeDevice,
    reconnectDevice,
    firmwareUpdateSuccessHandler,
    firmwareUpdateErrorHandler,
};
